// Lightbox Component - For previewing images, videos, maps, and embedded content
import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ExternalLink, Play, MapPin, Image as ImageIcon } from 'lucide-react';

const Lightbox = ({ isOpen, onClose, content, type, items, currentIndex, onNavigate }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && items && currentIndex > 0) onNavigate(currentIndex - 1);
      if (e.key === 'ArrowRight' && items && currentIndex < items.length - 1) onNavigate(currentIndex + 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, items]);

  if (!isOpen) return null;

  const renderContent = () => {
    switch (type) {
      case 'image':
        return (
          <img
            src={content}
            alt="Preview"
            className="max-w-full max-h-[80vh] object-contain rounded-lg"
            onLoad={() => setLoading(false)}
          />
        );
      case 'video':
        return (
          <div className="w-full max-w-4xl aspect-video">
            <iframe
              src={content.replace('watch?v=', 'embed/')}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={() => setLoading(false)}
            />
          </div>
        );
      case 'youtube':
        const videoId = content.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\s?]+)/)?.[1];
        return (
          <div className="w-full max-w-4xl aspect-video">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={() => setLoading(false)}
            />
          </div>
        );
      case 'map':
        return (
          <div className="w-full max-w-4xl h-[70vh]">
            <iframe
              src={content.includes('embed') ? content : `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(content)}`}
              className="w-full h-full rounded-lg"
              allowFullScreen
              loading="lazy"
              onLoad={() => setLoading(false)}
            />
          </div>
        );
      case 'product':
        return (
          <div className="glass-panel p-6 max-w-lg">
            {content.image && (
              <img src={content.image} alt={content.name} className="w-full h-64 object-cover rounded-lg mb-4" />
            )}
            <h3 className="text-xl font-bold text-white mb-2">{content.name}</h3>
            <p className="text-2xl font-bold text-red-400 mb-2">${content.price?.toLocaleString()} MXN</p>
            <p className="text-gray-400 mb-4">{content.store}</p>
            <a href={content.url} target="_blank" rel="noopener noreferrer" className="btn-primary w-full flex items-center justify-center gap-2">
              <ExternalLink size={18} />Ver en tienda
            </a>
          </div>
        );
      default:
        return (
          <div className="glass-panel p-8 text-center">
            <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Contenido no disponible</p>
          </div>
        );
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Close Button */}
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
      >
        <X className="text-white" size={24} />
      </button>

      {/* Navigation Arrows */}
      {items && items.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex - 1); }}
              className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronLeft className="text-white" size={24} />
            </button>
          )}
          {currentIndex < items.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex + 1); }}
              className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronRight className="text-white" size={24} />
            </button>
          )}
        </>
      )}

      {/* Content */}
      <div onClick={(e) => e.stopPropagation()}>
        {loading && type !== 'product' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {renderContent()}
      </div>

      {/* Counter */}
      {items && items.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 rounded-full text-white text-sm">
          {currentIndex + 1} / {items.length}
        </div>
      )}
    </div>
  );
};

export default Lightbox;
